import { getFontCss, styleConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectFilteredPeopleMembers } from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import AppName from 'src/components/AppName/AppName'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userEmailOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import {
  FormFieldsValidation,
  useBackgroundDispatch,
  useFormFields,
} from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import {
  getOwnUser,
  getRecentUsers,
} from 'src/redux/actions/data/users.actions'
import { LocationState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import { PutCreditCardRequest } from 'src/requests/putCreditCard.request'
import { stringArrayIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type BudgetUsersFields = {
  emails?: string[]
}

interface BudgetUserFormProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  suggestedProps?: {
    showSuggested: boolean
    showOwnUserInSuggested?: boolean
  }
  filterUserbyAssocationTypes?: UserAssociation[]
  unableToAddNewUser?: boolean
  tooltip?: React.ReactElement
  displayPills?: boolean
  initialFlatFields?: Partial<BudgetUsersFields>
  error?: string
  toggleAlert?: (hasUsers: boolean) => void
  displayAlert?: boolean
  putCreditCard: (body: Partial<PutCreditCardRequest>) => void
  creditCard: CreditCardListModel
}

const BudgetUsersForm: React.FC<BudgetUserFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const userEmailMap = useSelector(selectUserEmailMap)
  const memberListModels = useSelector(selectFilteredPeopleMembers)
  const user = useCurrentUser()
  const { analytics } = useAnalyticsContext()

  const location = useLocation<LocationState>()

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
  } = useFormFields<BudgetUsersFields>(
    {
      emails: location.state?.autofill ? [location.state.autofill] : [],
    },
    (): FormFieldsValidation<BudgetUsersFields> => {
      return {
        emails: [stringArrayIsEmpty],
      }
    },
  )

  const userEmailSet =
    flatFields.emails !== undefined
      ? new Set(flatFields.emails)
      : new Set<string>()

  const memberEmailMap = useMemo(() => {
    return Object.values(memberListModels).reduce(
      (acc: Record<string, UserListModel>, curr) => ({
        ...acc,
        [curr.email]: curr,
      }),
      {},
    )
  }, [memberListModels])

  const emailMap = useMemo(() => {
    return { ...userEmailMap, ...memberEmailMap }
  }, [userEmailMap, memberEmailMap])

  const selectedOptions: UserOption[] | undefined = useMemo(
    () =>
      flatFields.emails !== undefined
        ? flatFields.emails
            .filter((value) => emailMap[value] !== undefined)
            .map((value) => userEmailOption(emailMap[value]))
        : undefined,
    [flatFields.emails],
  )

  useEffect((): void => {
    if (props.suggestedProps && props.suggestedProps.showSuggested) {
      if (props.suggestedProps.showOwnUserInSuggested) {
        dispatch(getOwnUser())
      }
      if (props.filterUserbyAssocationTypes) {
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.virtualCardTo,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.virtualCardTo))
        }
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.virtualCardFrom,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.virtualCardFrom))
        }
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.delegateCreditCardTo,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.delegateCreditCardTo))
        }
      } else {
        dispatch(getRecentUsers(UserAssociation.virtualCardTo))
        dispatch(getRecentUsers(UserAssociation.invitationTo))
      }
    }
  }, [])

  const selectedEmails = useMemo(
    () => selectedOptions?.map((selectedOption) => selectedOption.value),
    [selectedOptions],
  )

  const selectedUsers = useMemo(
    () =>
      Object.values(emailMap).filter((user) =>
        selectedEmails?.includes(user.email),
      ),
    [selectedEmails, emailMap],
  )

  const newEmails = useMemo(
    () =>
      Array.from(userEmailSet)?.filter(
        (email) => Object.keys(emailMap).indexOf(email) < 0,
      ),
    [userEmailSet, emailMap],
  )

  const selectedUsersNotInOrg = useMemo(
    () =>
      selectedUsers
        ?.filter(
          (selectedUser) =>
            selectedUser?.organizationId !== user?.organizationId,
        )
        .map((user) => user.email)
        .concat(newEmails),
    [selectedUsers, user],
  )

  useEffect(() => {
    props.toggleAlert?.(selectedUsersNotInOrg.length > 0)
  }, [selectedUsersNotInOrg])

  const addDeleteVal = (newSet: Set<string>, val: string): void => {
    if (newSet.has(val)) {
      newSet.delete(val)
    } else {
      newSet.add(val)
    }
  }

  const onEmailChange = (val: string[]): void => {
    onFlatFieldChange('emails', val)
  }

  const handleEmailSelection = (val: string): void => {
    const newSet = new Set(flatFields.emails)
    addDeleteVal(newSet, val)
    onEmailChange(Array.from(newSet))
  }

  const handleMultiEmailSelection = (val: string[]): void => {
    const newSet = new Set(flatFields.emails)
    val.map((val) => {
      addDeleteVal(newSet, val)
    })
    onEmailChange(Array.from(newSet))
  }

  const onSuccessfulSubmit = (): void => {
    props.putCreditCard({
      emails: flatFields.emails,
    })
    analytics?.track('add budget user', {
      budget_id: props.creditCard?.id,
      usersAdded:
        flatFields.emails !== undefined
          ? flatFields.emails.map((email) => email.split('@')[1])
          : [],
      issuer: props.creditCard?.issuer,
    })
    onFlatFieldChange('emails', [])
  }

  return (
    <form onSubmit={onSuccessfulSubmit}>
      <div
        css={css`
          display: flex;
          align-items: start;
          gap: 20px;
          align-self: stretch;
          flex-direction: row;
          flex-shrink: 1;
        `}
      >
        <div
          css={css`
            width: 100%;
          `}
        >
          <UserDropdown
            css={css`
              &:nth-child(n + 2) {
                margin-top: 24px;
              }
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              z-index: 3;
            `}
            selectedOptions={selectedOptions}
            optionGroups={
              props.userOptions && props.userOptions.length > 0
                ? [
                    {
                      label:
                        user?.hasOrgAdminPermissions ||
                        user?.organizationRole === OrganizationRoles.cardManager
                          ? translate('${appName} Contacts', {
                              appName: <AppName />,
                            })
                          : translate('${appName} Org Members', {
                              appName: <AppName />,
                            }),
                      options: props.userOptions,
                    },
                  ]
                : []
            }
            recentOptionGroups={
              props.recentUserOptions && props.recentUserOptions.length > 0
                ? [
                    {
                      label: translate('Suggested', {
                        appName: <AppName />,
                      }),
                      options: props.recentUserOptions,
                      hideGroup: true,
                    },
                  ]
                : []
            }
            isCreatable
            isMulti
            showSuggested
            errors={flatFieldsErrors.emails}
            chooseOption={handleEmailSelection}
            chooseMultiOption={handleMultiEmailSelection}
            selectedValues={userEmailSet}
            placeholder={translateSingleString(
              locale,
              'Enter one or more emails',
            )}
            newOptionLabel={translate('New Contact')}
            infiniteScrollProps={props.userInfiniteScrollProps}
            displayPills
            showOutsideOrgAlert={
              selectedUsersNotInOrg?.length > 0
                ? translate(
                    "You've added people outside of your org as budget users. They must accept their org invite as a Member in order to access this budget.",
                  )
                : undefined
            }
            id={'budgetUsers'}
            preventGuests
          />
        </div>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 4px;
          `}
        >
          <Button
            css={css`
              width: 105px;
              padding: 12px 18px;
              margin-top: 6px;
            `}
            type="button"
            variant="primary"
            onClick={onSuccessfulSubmit}
            disabled={userEmailSet.size === 0}
            id="addButton"
          >
            {translate('Add')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default BudgetUsersForm
